import centersSeed from "@/data/centers.generated.json";
import React, { useEffect, useState } from "react";
import axios from "axios";
import { MapPin, Phone, MessageCircle, ExternalLink, Expand } from "lucide-react";
import { API, whatsappLink, IS_PRERENDER } from "@/lib/faro";
import { GALLERY_ALTS } from "@/lib/galleryAlts";
import { useMediaQuery } from "@/lib/useMediaQuery";
import { LazyMap } from "@/components/LazyMap";
import { trackClick } from "@/lib/analytics";
import Lightbox from "@/components/Lightbox";

function CenterGallery({ center, photoIdx, setPhotoIdx, onZoom }) {
  return (
    <div data-testid="center-gallery">
      <div className="relative h-72 bg-[var(--faro-blue)] overflow-hidden">
        <img
          src={center.images[photoIdx] || center.images[0]}
          alt={GALLERY_ALTS[center.images[photoIdx]] || `Autoescuela Faro ${center.name} — fachada e instalaciones del centro`}
          width="800"
          height="600"
          loading="lazy"
          className="w-full h-full object-cover"
          data-testid="center-photo"
        />
        <button
          type="button"
          onClick={onZoom}
          aria-label="Ver a pantalla completa"
          data-testid="center-zoom"
          className="absolute top-3 right-3 z-10 w-10 h-10 rounded-full bg-black/40 hover:bg-black/60 backdrop-blur text-white flex items-center justify-center transition-colors"
        >
          <Expand size={18} />
        </button>
      </div>
      {center.images.length > 1 && (
        <div className="flex gap-2 p-3 bg-[var(--faro-bg)]">
          {center.images.map((img, i) =>
            i === photoIdx ? null : (
              <button
                key={img}
                onClick={() => setPhotoIdx(i)}
                data-testid={`center-photo-thumb-${i}`}
                className="h-16 w-24 rounded-lg overflow-hidden border-2 border-transparent opacity-70 hover:opacity-100 transition-all"
              >
                <img src={img} alt={GALLERY_ALTS[img] || `Autoescuela Faro ${center.name} — foto ${i + 1} de las instalaciones`} width="400" height="300" loading="lazy" className="w-full h-full object-cover" />
              </button>
            )
          )}
        </div>
      )}
    </div>
  );
}

function CenterDetails({ center, photoIdx, setPhotoIdx, onZoom }) {
  return (
    <div className="rounded-3xl overflow-hidden border border-[rgba(30,42,143,0.12)] bg-[var(--faro-bg)] h-full">
      {center.images && center.images.length ? (
        <CenterGallery center={center} photoIdx={photoIdx} setPhotoIdx={setPhotoIdx} onZoom={onZoom} />
      ) : (
        <div className="relative h-72 bg-[var(--faro-blue)] faro-grid-bg">
          <LazyMap
            src={`https://www.google.com/maps?q=${encodeURIComponent(center.map_query || center.address)}&output=embed`}
            title={`Mapa ${center.name}`}
            testid="center-map"
            autoLoad
          />
        </div>
      )}
      <div className="p-6 sm:p-8">
        <div className="text-xs uppercase tracking-[0.2em] font-bold text-[var(--faro-red)]">
          {center.id === "plazaamerica" ? "Centro Recuperación de Puntos" : "Centro Faro"}
        </div>
        <h3 className="font-display font-black text-3xl mt-1">{center.name}</h3>
        <p className="text-[var(--faro-text-soft)] mt-2">{center.address}</p>

        <div className="mt-5 grid grid-cols-1 sm:grid-cols-2 gap-3">
          <a
            href={`tel:+34${String(center.phone_landline).replace(/\D/g, "")}`}
            data-testid="center-call"
            className="btn-pill btn-ghost justify-center"
          >
            <Phone size={16} /> {center.phone_landline}
          </a>
          <a
            href={whatsappLink(center.whatsapp, `Hola, me gustaría información del centro de ${center.name}`)}
            target="_blank"
            rel="noreferrer"
            data-testid="center-whatsapp"
            className="btn-pill justify-center"
            style={{ background: "#25d366", color: "#fff" }}
          >
            <MessageCircle size={16} /> WhatsApp
          </a>
        </div>

        <a
          href={center.maps_url}
          target="_blank"
          rel="noreferrer"
          data-testid="center-maps"
          className="mt-3 inline-flex items-center gap-1 text-sm font-semibold text-[var(--faro-blue)]"
        >
          Cómo llegar <ExternalLink size={14} />
        </a>

        {center.images && center.images.length > 0 && (
          <div className="mt-5 rounded-xl overflow-hidden border border-[rgba(30,42,143,0.12)] h-56">
            <LazyMap
              src={`https://www.google.com/maps?q=${encodeURIComponent(center.map_query || center.address)}&output=embed`}
              title={`Mapa ${center.name}`}
              testid="center-detail-map"
              autoLoad
            />
          </div>
        )}
      </div>
    </div>
  );
}

function CenterCard({ c, isActive, onToggle }) {
  return (
    <button
      data-testid={`center-${c.id}`}
      data-active={isActive}
      onClick={onToggle}
      className="center-card w-full text-left"
    >
      <div className="flex items-start justify-between gap-4">
        <div>
          <div className="text-xs uppercase tracking-[0.2em] font-bold opacity-80">Centro</div>
          <div className="font-display font-black text-2xl mt-1">{c.name}</div>
          <div className="text-sm mt-2 center-meta opacity-80">{c.address}</div>
        </div>
        <MapPin size={18} className="shrink-0 mt-1" />
      </div>
      <div className="mt-3 flex flex-wrap gap-2">
        {c.highlights.map((h) => (
          <React.Fragment key={h}>
            <span
              className={`text-[10px] uppercase tracking-[0.15em] font-bold px-2 py-1 rounded-full ${
                isActive ? "bg-white/15 text-white" : "bg-[var(--faro-bg)] text-[var(--faro-blue)]"
              }`}
            >
              {h}
            </span>{" "}
          </React.Fragment>
        ))}
      </div>
    </button>
  );
}

const CentersHeader = () => (
        <div className="flex flex-col lg:flex-row lg:items-end lg:justify-between gap-6 mb-10">
          <div className="max-w-2xl">
            <span className="eyebrow">Nuestros centros</span>
            <h2 className="font-display font-black tracking-tighter text-4xl sm:text-5xl mt-3 leading-tight">
              5 sedes cerca de ti.
            </h2>
            <p className="mt-4 text-lg text-[var(--faro-text-soft)]">
              Elige el centro que mejor te venga y empieza tu formación con el equipo de Faro.
            </p>
          </div>
        </div>
);

export default function Centers() {
  const [centers, setCenters] = useState(Array.isArray(centersSeed) ? centersSeed : []);
  const [activeId, setActiveId] = useState(Array.isArray(centersSeed) && centersSeed[0] ? centersSeed[0].id : null);
  const [photoIdx, setPhotoIdx] = useState(0);
  const [zoomOpen, setZoomOpen] = useState(false);
  const isDesktop = useMediaQuery("(min-width: 1024px)");

  useEffect(() => {
    axios.get(`${API}/centers`).then((r) => {
      setCenters(r.data);
      if (r.data[0]) setActiveId(r.data[0].id);
    }).catch(() => {});
  }, []);

  useEffect(() => { setPhotoIdx(0); }, [activeId]);

  const active = centers.find((c) => c.id === activeId);

  return (
    <section id="centros" className="section-pad bg-white" data-testid="centers-section">
      <div className="max-w-7xl mx-auto px-5 sm:px-8">
        <CentersHeader />

        <div className="grid grid-cols-1 lg:grid-cols-12 gap-6">
          <div className="lg:col-span-5 space-y-3" data-testid="centers-list">
            {centers.map((c) => (
              <React.Fragment key={c.id}>
                <CenterCard
                  c={c}
                  isActive={c.id === activeId}
                  onToggle={() => setActiveId((prev) => (prev === c.id ? null : c.id))}
                />

                {/* Inline expansion under the tapped card (single DOM instance on mobile) */}
                {!isDesktop && c.id === activeId && (
                  <div
                    className="pt-2 pb-2"
                    data-testid={`center-inline-details-${c.id}`}
                  >
                    <CenterDetails
                      center={c}
                      photoIdx={photoIdx}
                      setPhotoIdx={setPhotoIdx}
                      onZoom={() => setZoomOpen(true)}
                    />
                  </div>
                )}
              </React.Fragment>
            ))}
          </div>

          {/* Right column details (single DOM instance on desktop). Skipped during
              prerender so the active center's card exists exactly once in the crawled HTML. */}
          <div className="hidden lg:block lg:col-span-7" data-testid="center-detail">
            {isDesktop && active && !IS_PRERENDER && (
              <CenterDetails
                center={active}
                photoIdx={photoIdx}
                setPhotoIdx={setPhotoIdx}
                onZoom={() => setZoomOpen(true)}
              />
            )}
          </div>
        </div>
      </div>

      {active?.images?.length > 0 && (
        <Lightbox
          images={active.images}
          startIndex={photoIdx}
          open={zoomOpen}
          onClose={() => setZoomOpen(false)}
        />
      )}
    </section>
  );
}
